/**
 * =============================================================================
 *  KHO ANH - NHUNG MOT PNG / JPEG VAO TRANG MA KHONG NOI DOI VE GIA CUA NO
 * =============================================================================
 *
 *  Tep nay lam DUNG hai viec, va hai viec do la ly do no ton tai rieng:
 *
 *      1. GIAI MA THAT truoc khi cho phep chen.
 *      2. DO gia phai tra bang byte, truoc khi nguoi dung bam Luu.
 *
 * =============================================================================
 *  VI SAO KHONG CHI DOC PHAN DAU TEP
 * =============================================================================
 *  Doc chu ky PNG va khoi IHDR de lay kich thuoc la viec de, va no CHUNG MINH
 *  RAT IT: mot tep co dung tam byte dau van co the co phan du lieu cut ngang,
 *  bang mau hong, hoac kieu mau ma bo giai ma cua ta khong dung duoc. Luc do
 *  loi khong no ra o day - no no ra o giua duong luu, khi nguoi dung da ve xong
 *  ca trang va dang cho tep tai ve.
 *
 *  Day dung la bai hoc "kiem tra bang trich xuat khong chung minh duoc gi", chi
 *  doi sang anh. Nen o day ta chay CHINH BO GIAI MA cua duong ghi - pdf-lib
 *  embedPng / embedJpg - ngay tai luc ghi nhan. Cai gi qua duoc cho nay thi
 *  chac chan qua duoc luc luu, vi do la cung mot doan ma.
 *
 * =============================================================================
 *  GIA BANG BYTE: PNG CO THE PHINH TO RA, KHONG PHAI NHO DI
 * =============================================================================
 *  Truc giac thong thuong ("anh 2 MB thi tep to them 2 MB") SAI voi PNG.
 *  embedPng() giai nen anh ve pixel tho roi nen lai bang Flate. Anh chup
 *  12 megapixel co the la 3 MB tren dia va thanh HON 20 MB trong tep PDF.
 *  JPEG thi nguoc lai: byte duoc nhung NGUYEN VEN (/DCTDecode), nen gia dung
 *  bang kich thuoc tep.
 *
 *  Nen ta khong doan. Ta nhung thu vao mot tai lieu bo di va DO kich thuoc that.
 *  Con so bao cho nguoi dung la con so do.
 */
import { PDFDocument, type PDFImage } from 'pdf-lib';
import type { PdfCore, PdfPoint, PdfRect } from '@pdfeditor/types';

/** 72 diem = 1 inch. Anh man hinh quy uoc 96 dpi -> 0.75 diem moi diem anh. */
export const SCREEN_DPI = 96;
export const PT_PER_PIXEL = 72 / SCREEN_DPI;

/**
 * Tran so diem anh cho MOT anh. Tren muc nay thi phai giam mau truoc.
 *
 * 12 trieu diem anh (anh dien thoai thong thuong) nhung tho ra khoang 24 MB
 * du lieu pixel. Mot trang A4 in o 300 dpi chi chua duoc khoang 8.7 trieu diem
 * anh, nen phan vuot qua khong he hien ra tren giay - no chi lam tep nang len.
 */
export const MAX_IMAGE_PIXELS = 8_000_000;

/** Tren muc nay thi canh bao nguoi dung ve kich thuoc tep truoc khi chen. */
export const LARGE_EMBED_BYTES = 2_000_000;

export type ImageFormat = 'png' | 'jpeg';

export interface ImageAsset {
  /** Ma tra cuu, di theo `Annotation.imageRef`. */
  ref: string;
  format: ImageFormat;
  /** Byte GOC cua tep, da duoc chung minh la giai ma duoc. */
  bytes: Uint8Array;
  widthPx: number;
  heightPx: number;
  /** Kich thuoc tu nhien tren trang, tinh bang diem. */
  naturalWidthPt: number;
  naturalHeightPt: number;
  /** Kich thuoc tep goc. */
  sourceBytes: number;
  /**
   * So byte anh nay THUC SU them vao tep PDF, do bang phep nhung that.
   * Voi PNG con so nay thuong LON HON sourceBytes - xem ghi chu dau tep.
   */
  embeddedBytes: number;
  /** Nhan cho nguoi dung (ten tep). */
  label: string;
}

/**
 * VI SAO CO `code` BEN CANH `reason`.
 *
 * `reason` la cau noi cho NGUOI DUNG doc. Tang giao dien lai can biet mot dieu
 * KHAC: loi nay co loi thoat khong. Chi rieng "qua nhieu diem anh" la co - ta
 * de nghi giam mau roi chen lai. Phan biet hai truong hop do bang cach do khop
 * chuoi `reason` la buoc giao dien phu thuoc vao tung chu cua mot cau tieng
 * Viet: doi mot dau phay trong thong bao se lam mat duong giam mau, va khong bo
 * test nao thay.
 */
export type ImageRejectionCode =
  | 'empty'
  | 'unknown-format'
  | 'decode-failed'
  | 'bad-dimensions'
  | 'too-many-pixels';

/** Kich thuoc de nghi khi anh vuot tran diem anh. Di kem ma 'too-many-pixels'. */
export interface OversizeInfo {
  widthPx: number;
  heightPx: number;
  pixels: number;
  maxPixels: number;
  /** Kich thuoc sau khi giam, GIU DUNG TI LE. */
  targetWidthPx: number;
  targetHeightPx: number;
}

export type ImageRejection = {
  ok: false;
  code: ImageRejectionCode;
  reason: string;
  oversize?: OversizeInfo;
};
export type ImageAcceptance = { ok: true; asset: ImageAsset };
export type ImageResult = ImageAcceptance | ImageRejection;

function reject(code: ImageRejectionCode, reason: string): ImageRejection {
  return { ok: false, code, reason };
}

/**
 * Nhan dang dinh dang tu BYTE, khong tu duoi tep.
 *
 * Duoi tep la thu nguoi dung go ra: mot tep ".png" chua du lieu JPEG la chuyen
 * hang ngay (doi ten tay, tai ve tu web). Chon bo giai ma theo duoi tep se cho
 * ra mot loi kho hieu tu sau trong pdf-lib thay vi mot cau tra loi ro rang.
 */
export function sniffFormat(bytes: Uint8Array): ImageFormat | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'png';
  }
  // JPEG: SOI (FFD8) roi ngay mot dau phan doan (FF..).
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'jpeg';
  }
  return null;
}

/** Nhan doc duoc cho nguoi dung, dang "1,2 MB". */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`.replace('.', ',');
}

let counter = 0;

/**
 * GHI NHAN mot anh: giai ma that, do gia that, roi moi tra ve ma tra cuu.
 *
 * Tra ve mot LY DO TIENG VIET khi tu choi. Khong bao giờ nem, va khong bao gio
 * tra ve mot ma tra cuu cho mot anh ma duong ghi se nghen o buoc sau.
 */
export async function registerImage(
  input: Uint8Array,
  label: string,
  options: { maxPixels?: number } = {},
): Promise<ImageResult> {
  const bytes = input.slice();
  if (bytes.length === 0) {
    return reject('empty', `Tệp “${label}” rỗng.`);
  }

  const format = sniffFormat(bytes);
  if (!format) {
    return reject(
      'unknown-format',
      `Tệp “${label}” không phải PNG hay JPEG. Bản web chỉ nhúng được hai định dạng này, ` +
        'vì đó là hai định dạng PDF mang được nguyên vẹn. Ảnh GIF, WebP, HEIC hay SVG cần ' +
        'được chuyển sang PNG trước.',
    );
  }

  // Nhung THAT vao mot tai lieu bo di. Day vua la phep giai ma, vua la phep do.
  let probe: PDFDocument;
  let image: PDFImage;
  try {
    probe = await PDFDocument.create({ updateMetadata: false });
    image = format === 'png' ? await probe.embedPng(bytes) : await probe.embedJpg(bytes);
  } catch (err) {
    return reject(
      'decode-failed',
      `Không giải mã được ảnh “${label}”: ${String(err).slice(0, 120)}. ` +
        'Tệp có thể hỏng, bị cắt ngang, hoặc dùng kiểu màu mà bản web chưa đọc được.',
    );
  }

  const widthPx = image.width;
  const heightPx = image.height;
  if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx < 1 || heightPx < 1) {
    return reject('bad-dimensions', `Ảnh “${label}” không có kích thước hợp lệ.`);
  }

  const maxPixels = options.maxPixels ?? MAX_IMAGE_PIXELS;
  const pixels = widthPx * heightPx;
  if (pixels > maxPixels) {
    const mp = (pixels / 1_000_000).toFixed(1).replace('.', ',');
    const limitMp = (maxPixels / 1_000_000).toFixed(0);
    const target = planDownsample(widthPx, heightPx, maxPixels);
    const rejection = reject(
      'too-many-pixels',
      `Ảnh “${label}” quá lớn: ${widthPx}×${heightPx} (${mp} triệu điểm ảnh, giới hạn ${limitMp} triệu). ` +
        'Một trang A4 in ở 300 dpi chỉ chứa được khoảng 8,7 triệu điểm ảnh, nên phần vượt quá ' +
        'KHÔNG hiện ra trên giấy — nó chỉ làm tệp nặng thêm.',
    );
    // Kem theo kich thuoc DE NGHI. Tang giao dien dung con so nay de hoi nguoi
    // dung mot cau tra loi duoc ("giảm xuống 3264×2448 rồi chèn?") thay vi day
    // ho ra ngoai voi mot loi tu choi va mot bai tap ve nha.
    rejection.oversize = {
      widthPx,
      heightPx,
      pixels,
      maxPixels,
      targetWidthPx: target.widthPx,
      targetHeightPx: target.heightPx,
    };
    return rejection;
  }

  // Kich thuoc that ma anh nay them vao tep. Do bang hieu so, khong doan.
  //
  // MOC SO SANH PHAI LA MOT TAI LIEU KHAC. pdf-lib xa MOI anh da embed ra tep
  // khi save(), ke ca anh chua he duoc ve. Do "probe truoc khi them trang" roi
  // tru di, nhu ban dau, thi so bi tru DA chua ca buc anh - hieu so con lai chi
  // la mot trang trong (~350 B) va bao cao ra con so do cho MOI buc anh, bat ke
  // to nho. Do la mot lo^i im lang: nguoi dung nhan "khoang 350 B" roi mo ra
  // mot tep nang hon nhieu megabyte.
  const baseline = await PDFDocument.create({ updateMetadata: false });
  baseline.addPage([10, 10]);
  const baselineLength = (await baseline.save({ useObjectStreams: false })).length;

  const page = probe.addPage([10, 10]);
  page.drawImage(image, { x: 0, y: 0, width: 1, height: 1 });
  const withImageLength = (await probe.save({ useObjectStreams: false })).length;
  const embeddedBytes = Math.max(0, withImageLength - baselineLength);

  counter += 1;
  return {
    ok: true,
    asset: {
      ref: `img-${counter}`,
      format,
      bytes,
      widthPx,
      heightPx,
      naturalWidthPt: widthPx * PT_PER_PIXEL,
      naturalHeightPt: heightPx * PT_PER_PIXEL,
      sourceBytes: bytes.length,
      embeddedBytes,
      label,
    },
  };
}

/**
 * Cau thong bao ve gia phai tra, TIENG VIET, hien truoc khi chen.
 *
 * Tra ve null khi anh du nho de khong dang noi. Im lang o day la dung: canh bao
 * moi anh mot lan se lam nguoi dung hoc cach bo qua canh bao.
 */
export function sizeNotice(asset: ImageAsset): string | null {
  if (asset.embeddedBytes < LARGE_EMBED_BYTES) return null;
  const grew = asset.embeddedBytes > asset.sourceBytes * 1.2;
  const base =
    `Ảnh “${asset.label}” (${asset.widthPx}×${asset.heightPx}, ${formatBytes(asset.sourceBytes)}) ` +
    `sẽ làm tệp PDF nặng thêm khoảng ${formatBytes(asset.embeddedBytes)}.`;
  if (!grew || asset.format !== 'png') return base;
  return (
    `${base} PNG được nhúng dưới dạng điểm ảnh thô rồi nén lại, nên phần thêm vào LỚN HƠN ` +
    'chính tệp ảnh. Lưu ảnh sang JPEG trước khi chèn sẽ nhẹ hơn nhiều.'
  );
}

// ===========================================================================
//  GIAM MAU - LOI THOAT DUY NHAT CHO MOT BUC ANH QUA LON
// ===========================================================================
//
//  Ba cach xu ly mot buc anh 12 megapixel, va hai trong so do la sai:
//
//    1. NHUNG NGUYEN. Tep PDF phinh len hai chuc megabyte de mang mot luong
//       chi tiet ma to giay khong bao gio in ra duoc. Am tham lam nang tep cua
//       nguoi dung.
//    2. TU DONG GIAM. Nguoi dung chen anh cua ho va nhan lai mot buc anh KHAC.
//       Am tham sua du lieu cua nguoi khac.
//    3. HOI. Noi ro so do: "12 triệu điểm ảnh, sẽ giảm còn 8 triệu". Nguoi dung
//       chon. Do la duong duy nhat khong noi doi ve mot trong hai huong.
//
//  Nen o day chi cung cap CONG CU. Quyet dinh nam o tang giao dien, va giao
//  dien buoc phai noi ra minh da lam gi - xem describeDownsample().

/** Kich thuoc dich sau khi giam. */
export interface ResampleTarget {
  widthPx: number;
  heightPx: number;
}

export type ResampleResult = { ok: true; bytes: Uint8Array } | { ok: false; reason: string };

/**
 * Ham giam mau. Tach thanh mot kieu ham chu khong goi thang createImageBitmap:
 * phep giam mau la viec cua TRINH DUYET (giai ma anh + ve lai + ma hoa lai), va
 * bo test chay tren Node khong co ba thu do. Nhan vao mot ham thi phan QUYET
 * DINH - giam bao nhieu, noi gi voi nguoi dung - van kiem chung duoc, con phan
 * raster thi duoc thu tren trinh duyet that.
 */
export type Resampler = (
  bytes: Uint8Array,
  format: ImageFormat,
  target: ResampleTarget,
) => Promise<ResampleResult>;

/**
 * Kich thuoc lon nhat GIU DUNG TI LE ma van nam duoi tran diem anh.
 *
 * Ham thuan tuy. Lam tron XUONG roi kiem lai: sqrt cua mot ti le roi lam tron
 * co the vuot tran mot vai diem anh, va "vuot mot chut" o day nghia la lan ghi
 * nhan thu hai lai bi tu choi - nguoi dung dong y giam mau roi van khong chen
 * duoc, ma khong hieu tai sao.
 */
export function planDownsample(
  widthPx: number,
  heightPx: number,
  maxPixels: number,
): ResampleTarget {
  if (widthPx * heightPx <= maxPixels) return { widthPx, heightPx };
  const scale = Math.sqrt(maxPixels / (widthPx * heightPx));
  let w = Math.max(1, Math.floor(widthPx * scale));
  let h = Math.max(1, Math.floor(heightPx * scale));
  // Vong lui: co cung khong qua vai buoc, va no CHOT dieu kien thay vi hy vong.
  while (w * h > maxPixels && (w > 1 || h > 1)) {
    if (w >= h) w = Math.max(1, w - 1);
    else h = Math.max(1, h - 1);
  }
  return { widthPx: w, heightPx: h };
}

/**
 * Cau noi ro DA LAM GI voi anh cua nguoi dung. Bat buoc hien sau khi giam mau.
 *
 * Khong co cau nay thi viec giam mau tro thanh mot lan sua du lieu am tham, va
 * do dung la thu ca tep nay duoc viet ra de tranh.
 */
export function describeDownsample(
  label: string,
  from: ResampleTarget,
  to: ResampleTarget,
  asset: ImageAsset,
): string {
  return (
    `Đã giảm kích thước ảnh “${label}” từ ${from.widthPx}×${from.heightPx} xuống ` +
    `${to.widthPx}×${to.heightPx} trước khi chèn. Ảnh gốc trên đĩa của bạn KHÔNG bị sửa. ` +
    `Bản đã giảm làm tệp PDF nặng thêm khoảng ${formatBytes(asset.embeddedBytes)}.`
  );
}

/**
 * Bo giam mau cua TRINH DUYET. Tra ve ly do doc duoc thay vi nem.
 *
 * GIU NGUYEN DINH DANG, co y: doi PNG sang JPEG se lam mat kenh trong suot, va
 * mot anh nen trong bong nhien co nen den la mot lan "sua du lieu am tham" nua -
 * dung loai loi ma ca duong nay duoc dung len de chan.
 */
export const browserResampler: Resampler = async (bytes, format, target) => {
  const g = globalThis as {
    createImageBitmap?: typeof createImageBitmap;
    OffscreenCanvas?: typeof OffscreenCanvas;
  };
  if (typeof g.createImageBitmap !== 'function' || typeof g.OffscreenCanvas !== 'function') {
    return {
      ok: false,
      reason:
        'Trình duyệt này không có createImageBitmap/OffscreenCanvas nên bản web không tự giảm ' +
        'kích thước ảnh được. Hãy giảm ảnh bằng một công cụ khác rồi chèn lại.',
    };
  }

  const mime = format === 'png' ? 'image/png' : 'image/jpeg';
  try {
    // `bytes.slice()` chu khong phai chinh `bytes`: Blob giu tham chieu toi bo
    // dem, va bo dem do con duoc duong ghi nhan dung lai.
    const bitmap = await g.createImageBitmap(new Blob([bytes.slice()], { type: mime }), {
      resizeWidth: target.widthPx,
      resizeHeight: target.heightPx,
      resizeQuality: 'high',
    });
    const canvas = new g.OffscreenCanvas(target.widthPx, target.heightPx);
    const ctx = canvas.getContext('2d');
    if (!ctx) return { ok: false, reason: 'Không mở được ngữ cảnh vẽ 2D để giảm kích thước ảnh.' };
    (ctx as OffscreenCanvasRenderingContext2D).drawImage(
      bitmap,
      0,
      0,
      target.widthPx,
      target.heightPx,
    );
    bitmap.close();
    const blob = await canvas.convertToBlob({ type: mime, quality: 0.9 });
    return { ok: true, bytes: new Uint8Array(await blob.arrayBuffer()) };
  } catch (err) {
    return { ok: false, reason: `Không giảm được kích thước ảnh: ${String(err).slice(0, 120)}` };
  }
};

// ===========================================================================
//  TIEP NHAN MOT TEP ANH - TOAN BO CAY QUYET DINH, O MOT CHO
// ===========================================================================
//
//  Ham nay la thu ma nut "Chèn ảnh" goi. No song o day chu khong trong App.tsx
//  vi mot ly do da tra gia nhieu lan trong du an nay: mot cay quyet dinh nam
//  trong mot thanh phan React chi kiem chung duoc bang cach dung ca trinh duyet
//  len, nen tren thuc te no khong duoc kiem chung. O day no la mot ham thuan
//  tuy tren cac phu thuoc duoc TIEM VAO, va bo test chay het moi nhanh:
//  tu choi vi dinh dang, tu choi vi hong, dong y giam mau, tu choi giam mau,
//  giam mau that bai.
//
//  `notices` KHONG BAO GIO rong mot cach am tham khi co viec da xay ra: neu anh
//  bi giam mau, cau noi ro dieu do nam trong do va ben goi buoc phai hien no.

export interface ImageIntakeDeps {
  /** Ghi nhan byte vao kho. Thuong la `store.add` cua loi. */
  register: (bytes: Uint8Array, label: string) => Promise<ImageResult>;
  /** Hoi nguoi dung co dong y giam mau khong. Tra ve false = huy, khong chen. */
  confirmDownsample: (info: OversizeInfo, label: string) => boolean | Promise<boolean>;
  /** Bo giam mau. Mac dinh la bo cua trinh duyet. */
  resample?: Resampler;
}

export type ImageIntakeResult =
  | { ok: true; asset: ImageAsset; notices: string[] }
  | { ok: false; reason: string | null };

/**
 * `reason === null` nghia la NGUOI DUNG TU HUY - khong phai loi, va ben goi
 * khong duoc hien thong bao do nhu mot that bai.
 */
export async function intakeImage(
  bytes: Uint8Array,
  label: string,
  deps: ImageIntakeDeps,
): Promise<ImageIntakeResult> {
  const first = await deps.register(bytes, label);
  if (first.ok) {
    const notice = sizeNotice(first.asset);
    return { ok: true, asset: first.asset, notices: notice ? [notice] : [] };
  }
  if (first.code !== 'too-many-pixels' || !first.oversize) {
    return { ok: false, reason: first.reason };
  }

  const info = first.oversize;
  const agreed = await deps.confirmDownsample(info, label);
  if (!agreed) return { ok: false, reason: null };

  const format = sniffFormat(bytes);
  if (!format) return { ok: false, reason: first.reason };

  const resample = deps.resample ?? browserResampler;
  const target: ResampleTarget = { widthPx: info.targetWidthPx, heightPx: info.targetHeightPx };
  const resized = await resample(bytes, format, target);
  if (!resized.ok) return { ok: false, reason: resized.reason };

  // Ghi nhan LAI, qua dung cua kiem cu. Ban da giam mau khong duoc mien kiem
  // tra: mot bo ma hoa tra ve byte hong van la byte hong, va cho duy nhat bat
  // duoc dieu do la chinh bo giai ma cua duong ghi.
  const second = await deps.register(resized.bytes, label);
  if (!second.ok) {
    return {
      ok: false,
      reason: `Giảm kích thước xong nhưng bản mới vẫn không dùng được: ${second.reason}`,
    };
  }

  const notices = [
    describeDownsample(
      label,
      { widthPx: info.widthPx, heightPx: info.heightPx },
      target,
      second.asset,
    ),
  ];
  const extra = sizeNotice(second.asset);
  if (extra) notices.push(extra);
  return { ok: true, asset: second.asset, notices };
}

/**
 * Kho anh cua mot phien lam viec.
 *
 * Song ngoai danh sach lenh co chu dich: xem ghi chu `imageRef` trong hop dong
 * kieu. Lenh mang MA, kho nay mang BYTE.
 */
export class ImageStore {
  private readonly assets = new Map<string, ImageAsset>();

  async add(bytes: Uint8Array, label: string, options?: { maxPixels?: number }): Promise<ImageResult> {
    const result = await registerImage(bytes, label, options ?? {});
    if (result.ok) this.assets.set(result.asset.ref, result.asset);
    return result;
  }

  get(ref: string): ImageAsset | undefined {
    return this.assets.get(ref);
  }

  /** Ban do dua thang cho writeAnnotations(). */
  snapshot(): Map<string, ImageAsset> {
    return new Map(this.assets);
  }

  get size(): number {
    return this.assets.size;
  }
}

/**
 * Hinh chu nhat dat anh, GIU DUNG TI LE.
 *
 * `box` la vung nguoi dung keo ra. Anh duoc dat VUA TRONG vung do va canh giua,
 * chu khong bi keo gian. Do la mac dinh; nguoi dung giu phim de bop meo thi ben
 * goi dung thang `box`.
 */
export function fitPreservingAspect(
  box: { x: number; y: number; width: number; height: number },
  asset: { naturalWidthPt: number; naturalHeightPt: number },
): { x: number; y: number; width: number; height: number } {
  const ratio = asset.naturalWidthPt / asset.naturalHeightPt;
  if (!Number.isFinite(ratio) || ratio <= 0 || box.width <= 0 || box.height <= 0) return box;

  let width = box.width;
  let height = width / ratio;
  if (height > box.height) {
    height = box.height;
    width = height * ratio;
  }
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

/**
 * =============================================================================
 *  HINH CHU NHAT CUOI CUNG CUA MOT LAN DAT ANH - MOT PHEP TINH DUY NHAT
 * =============================================================================
 *
 *  Ban xem truoc tren man hinh va doi tuong /Stamp trong tep PHAI ra tu dung
 *  mot ham. Day la bai hoc so 3 cua du an, da tra gia hai lan (hinh chu nhat
 *  che chu, roi ghi chu 18 diem anh tren man hinh doi lai mot hop 160 diem
 *  trong tep). O day khong co cho de troi: lop hien thi ve tu `annotation.rect`,
 *  va `annotation.rect` chinh la thu ham nay tra ve.
 *
 *  BA truong hop, phan biet bang dau vao chu khong bang mot co rieng:
 *
 *    box === null   - nguoi dung chi BAM. Anh dat o co tu nhien 72 dpi, diem
 *                     bam la goc TREN-TRAI (chu khong phai tam): chu va anh moc
 *                     xuong tu cho bam, giong moi trinh soan thao khac.
 *    keepAspect     - MAC DINH khi keo. Anh nam VUA TRONG khung va canh giua.
 *    !keepAspect    - nguoi dung giu Shift de bop meo. Dung thang khung ho keo.
 */
export function placementRect(
  asset: Pick<ImageAsset, 'naturalWidthPt' | 'naturalHeightPt'>,
  point: PdfPoint,
  box: PdfRect | null,
  keepAspect: boolean,
): PdfRect {
  if (!box || box.width <= 0 || box.height <= 0) {
    return {
      x: point.x,
      y: point.y - asset.naturalHeightPt,
      width: asset.naturalWidthPt,
      height: asset.naturalHeightPt,
    };
  }
  return keepAspect ? fitPreservingAspect(box, asset) : box;
}

/**
 * NANG LUC CHEN ANH cua mot loi cu the.
 *
 * Cung khuon mau voi asPageOpsCore() trong core/pages/capability.ts, va vi cung
 * ly do: hop dong kieu dung chung khong co cho de "ghi nhan byte anh" - do la
 * viec cua tang ung dung. Loi khong co hai ham nay thi nut Chèn ảnh tu khoa lai
 * kem ly do, chu khong bam vao roi that bai.
 */
export interface ImageCapableCore {
  registerImage(bytes: Uint8Array, label: string): Promise<ImageResult>;
  imageAsset(ref: string): ImageAsset | undefined;
}

export function asImageCore(core: PdfCore | null): ImageCapableCore | null {
  if (!core) return null;
  const candidate = core as unknown as Partial<ImageCapableCore>;
  return typeof candidate.registerImage === 'function' && typeof candidate.imageAsset === 'function'
    ? (candidate as ImageCapableCore)
    : null;
}
